<template>
  <div class="stock-customers-page">
    <section class="hero-banner">
      <div class="banner-content">
        <div class="text-content">
          <h1 class="brand-name">{{ c.hero.brandName }}</h1>
          <h2 class="main-title">{{ c.hero.mainTitle }}</h2>
          <p class="subtitle">{{ c.hero.subtitle }}</p>
        </div>
      </div>
    </section>

    <section class="longbridge-advantages py-16 bg-white">
      <div class="max-w-7xl mx-auto px-4 lg:px-8">
        <div class="text-center mb-12">
          <h2 class="text-3xl lg:text-4xl font-bold text-gray-900">{{ c.audiences.title }}</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          <div v-for="item in c.audiences.items" :key="item.alt" class="advantage-card flex items-start space-x-4">
            <div class="icon-container">
              <img :src="item.icon" :alt="item.alt">
            </div>
            <div class="flex-1">
              <p class="text-gray-900 text-base leading-relaxed">{{ item.text }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="exclusive-privileges py-16 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div class="privilege-cards">
            <img src="@/assets/11f19c2a8408ff9cd9e8975bf4d617ac.png" :alt="c.riskInsights.imageAlt">
          </div>
          <div class="text-content">
            <h3 class="text-3xl font-bold text-gray-900 mb-6">{{ c.riskInsights.title }}</h3>
            <ul class="space-y-4 mb-6">
              <li v-for="point in c.riskInsights.points" :key="point" class="flex items-start">
                <span class="w-2 h-2 bg-[#ef7c5b] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span class="text-gray-700 leading-relaxed">{{ point }}</span>
              </li>
            </ul>
            <div class="highlight-box p-4 pl-0 mb-6">
              <p class="text-[#ef7c5b] font-semibold text-lg">{{ c.riskInsights.highlight }}</p>
            </div>
            <a :href="resolveLink(externalLinks.privilegeLearnMore)" target="_blank" rel="noopener noreferrer" class="bg-[#ef7c5b] cursor-pointer text-white px-8 py-2 rounded-3xl font-semibold transition-colors inline-block text-center">{{ c.riskInsights.buttonText }}</a>
          </div>
        </div>
      </div>
    </section>

    <section class="investor-tools py-16 bg-white">
      <div class="max-w-7xl mx-auto px-4 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div class="text-content">
            <h3 class="text-3xl font-bold text-gray-900 mb-6">{{ c.processFlow.title }}</h3>
            <ul class="space-y-6">
              <li v-for="item in c.processFlow.items" :key="item.title" class="flex items-start">
                <span class="w-3 h-3 bg-orange-500 rounded-full mt-2 mr-4 flex-shrink-0"></span>
                <div>
                  <p class="text-gray-900 font-semibold mb-1">{{ item.title }}</p>
                  <p class="text-gray-700 leading-relaxed">{{ item.desc }}</p>
                </div>
              </li>
            </ul>
            <a :href="resolveLink(externalLinks.investorTools)" target="_blank" rel="noopener noreferrer" class="bg-[#ef7c5b] cursor-pointer text-white px-8 py-2 mt-4 rounded-3xl font-semibold transition-colors inline-block text-center">{{ c.processFlow.buttonText }}</a>
          </div>
          <div class="phone-showcase flex justify-center">
            <img src="@/assets/514464cfbcd130f45e3951737a27355d.png" :alt="c.processFlow.imageAlt">
          </div>
        </div>
      </div>
    </section>

    <section class="tech-advantages py-16 bg-white">
      <div class="max-w-7xl mx-auto px-4 lg:px-8">
        <div class="mb-6">
          <h2 class="text-3xl lg:text-4xl font-bold text-gray-900">{{ c.tech.title }}</h2>
          <p class="text-base text-gray-800 mt-2">{{ c.tech.subtitle }}</p>
        </div>
        <div class="flex gap-6 mb-6 items-center text-gray-600">
          <button v-for="tab in c.tech.tabs" :key="tab.key" class="nav-tab" :class="{ active: currentTechTab===tab.key }" @click="currentTechTab=tab.key">{{ tab.label }}</button>
        </div>
        <div class="border-b border-gray-200 mb-8"></div>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div class="tech-card">
            <div class="tech-card-body">
              <div class="tech-title">{{ currentTech.cardLeft.title }}</div>
              <div class="tech-subtitle">{{ currentTech.cardLeft.subtitle }}</div>
            </div>
          </div>
          <div class="tech-card">
            <div class="tech-card-body">
              <div class="tech-title">{{ currentTech.cardRight.title }}</div>
              <div class="tech-subtitle">{{ currentTech.cardRight.subtitle }}</div>
            </div>
          </div>
        </div>
        <p class="text-gray-800 mb-4">{{ currentTech.intro }}</p>
        <ul class="space-y-6 mb-8">
          <li v-for="(adv, idx) in currentTech.advantages" :key="idx" class="flex items-start">
            <span class="advantage-icon-box">
              <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="#ef7c5b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle v-if="idx===0" cx="12" cy="12" r="3"/>
                <path v-if="idx===0" d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009.4 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 01-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9.4a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z"/>
                <path v-if="idx===1" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <template v-if="idx===2">
                  <rect x="3" y="3" width="18" height="14" rx="2" ry="2"/>
                  <path d="M7 19h10M9 7l2 2 4-4"/>
                </template>
              </svg>
            </span>
            <span class="text-gray-800">{{ adv }}</span>
          </li>
        </ul>
        <div class="text-center">
          <a :href="resolveLink(externalLinks.techLearnMore)" target="_blank" rel="noopener noreferrer" class="learn-more-btn inline-block">{{ c.tech.learnMore }}</a>
        </div>
        <div class="mt-8 text-center text-gray-500">{{ c.tech.footnote }}</div>
      </div>
    </section>

    <section class="trading-capabilities py-16 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 lg:px-8">
        <div class="text-center mb-12">
          <h2 class="text-3xl lg:text-4xl font-bold text-gray-900">{{ c.services.title }}</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="card in c.services.cards" :key="card.title" class="capability-card bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
            <div class="flex items-start gap-4">
              <img :src="card.icon" class="w-13 h-13" :alt="card.alt">
              <div class="flex-1">
                <h3 class="text-xl font-semibold text-gray-900 mb-2">{{ card.title }}</h3>
                <ul class="text-gray-700 space-y-1 text-sm">
                  <li v-for="point in card.points" :key="point">• {{ point }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="account-types py-16 bg白色">
      <div class="max-w-7xl mx-auto px-4 lg:px-8 text-center">
        <h2 class="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">{{ c.accounts.title }}</h2>
        <p class="text-base text-gray-800 mb-8">{{ c.accounts.subtitle }}</p>
        <div class="flex items-center justify-center gap-12 mb-6">
          <div v-for="item in c.accounts.items" :key="item.label" class="flex flex-col items-center">
            <img :src="item.icon" :alt="item.alt">
            <div class="text-gray-900">{{ item.label }}</div>
          </div>
        </div>
        <div class="max-w-3xl mx-auto border border-gray-200 rounded-lg p-6 bg-white text-left">
          <h4 class="font-semibold text-gray-900 mb-2">{{ c.accounts.org.title }}</h4>
          <p class="text-gray-700 text-sm leading-relaxed">{{ c.accounts.org.desc }}</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'InsuranceCustomers',
  metaInfo() {
    return {
      title: this.c.metaTitle
    };
  },
  data() {
    return {
      currentLocale: 'zh',
      defaultTradingUrl: 'https://redland.trade.wbrks.com/Webtrading',
      externalLinks: {
        privilegeLearnMore: '',
        investorTools: '',
        techLearnMore: ''
      },
      currentTechTab: 'cloud',
      messages: {
        zh: {
          metaTitle: '保险客户 - 红土证券',
          hero: {
            brandName: '红土证券',
            mainTitle: '保险客户',
            subtitle: '本页面内容由集团关联公司红土财富提供'
          },
          audiences: {
            title: '专业保险经纪覆盖全方位人群',
            items: [
              { icon: '/assets/bz1.png', alt: '低费率', text: '高净值家庭与个人：需要定制化方案，如家族信托、跨境资产配置，注重私密性与长期服务' },
              { icon: '/assets/bz2.png', alt: '专业工具', text: '成长型年轻家庭：首次置业、新婚或育儿阶段的家庭，需求集中在重疾险、医疗险、定期寿险、教育金，注重性价比，偏好线上沟通与灵活缴费方式' },
              { icon: '/assets/bz3.png', alt: '广泛市场', text: '小微企业主与创业者：关注企业财产险、雇主责任险、团体健康险，需平衡个人与企业资产隔离（如股东互保、寿险融资），对条款灵活性和理赔效率要求高' },
              { icon: '/assets/bz4.png', alt: '便捷出入金', text: '专业人士与高知群体：医生、律师、企业高管等高收入专业人士，偏好数据化分析、条款深度解读，关注全球医疗资源、高端养老服务' },
              { icon: '/assets/bz5.png', alt: '个性化服务', text: '特定行业从业人员：高风险职业（如建筑、货运）、自由职业者，需定制意外险、职业责任险，常被标准产品排除或加费' },
              { icon: '/assets/bz5.png', alt: '安全保障', text: '互联网原住民与新中产：习惯线上比价、自主研究条款，关注消费型保险、短期健康险、互联网定制产品，注重服务响应速度与线上工具使用体验' }
            ]
          },
          riskInsights: {
            title: '您的核心风险与需求洞察',
            imageAlt: '股票客户特权',
            points: [
              '健康透支风险：高强度工作、不规律作息，需防范重疾与医疗费用对储蓄的冲击。',
              '家庭责任初显：可能开始赡养父母或组建家庭，需一份高杠杆的意外与寿险保障。',
              '现金流管理：收入增长期，需平衡保障与储蓄，偏好灵活、高性价比的产品组合。'
            ],
            highlight: '深入沟通，精准识别您的家庭结构、财务状况与核心风险，奠定科学规划基础。',
            buttonText: '了解详情'
          },
          processFlow: {
            title: '以流程见证专业，以透明构建信任需求分析',
            imageAlt: '工具展示',
            items: [
              { title: '协助投保', desc: '全程指导填写投保资料，协助健康告知，确保流程合规，从源头规避未来纠纷。' },
              { title: '保单托管', desc: '为您电子化归档所有保单，清晰呈现保障全景，关键日期智能提醒，确保保障持续有效。' },
              { title: '协助理赔与维权', desc: '出险时第一时间响应，全程指导并代办理赔，遇争议时以专业立场为您据理力争。' }
            ],
            buttonText: '了解详情'
          },
          tech: {
            title: '专业的保险经纪服务体系',
            subtitle: '贴心为你搭配专属保障，智能守护每一次选择',
            learnMore: '了解更多',
            footnote: '值得信赖的证券经纪服务商',
            tabs: [
              { key: 'cloud', label: '产品动态配置' },
              { key: 'order', label: '保费与赔付管理' },
              { key: 'market', label: '风控与合规审计' },
              { key: 'funds', label: '业务一致性保障' }
            ],
            content: {
              cloud: {
                cardLeft: { title: '保险风控', subtitle: '购力计算' },
                cardRight: { title: '资产可视', subtitle: '风险先知' },
                intro: '支持个性化产品组合与动态定价策略，提升方案配置效率与市场响应能力',
                advantages: [
                  '专业团队支持根据客户专业定制、风险偏好、财务状况进行产品推荐',
                  '保费测算与风险定价模型，基于市场动态核保规则生成报价',
                  '多维度风险校验与合规筛查，确保方案符合监管与机构承保要求'
                ]
              },
              order: {
                cardLeft: { title: '动态核保', subtitle: '精准赔付' },
                cardRight: { title: '保费精算', subtitle: '风控闭环' },
                intro: '精算模型优化赔付方案，同步提升风险控制精度与理赔服务效率',
                advantages: [
                  '支持批量报价、阶梯费率、续保优惠等复杂计费方案定制',
                  '提供线下风险评估、理赔争议协商与财务对账服务',
                  '执行人工资金清分、单证核对与机构间账务稽核'
                ]
              },
              market: {
                cardLeft: { title: '全链审计', subtitle: '风控随行' },
                cardRight: { title: '合规同步', subtitle: '风险洞见' },
                intro: '数据一致性、监管合规化、多源风控与智能审计机制结合，构建全面业务风险防线',
                advantages: [
                  '执行人工尽职调查、背景核实与可疑交易线下审查',
                  '支持黑名单筛查、反洗钱监测、客户适当性管理，确保全流程业务合规',
                  '支持多维度审计查询、异常操作告警与自动化合规证据收集'
                ]
              },
              funds: {
                cardLeft: { title: '业务同源', subtitle: '风控一体' },
                cardRight: { title: '跨链一致', subtitle: '流程互通' },
                intro: '建立全周期业务连续流程，在关键节点执行人工交叉验证，保障信息一致性。',
                advantages: [
                  '使用统一的线下流程引擎，确保投保单、核保、批改和理赔各环节状态同步更新。',
                  '设立关键操作线下复核与审批环节，确保步骤合规且流程衔接顺畅。',
                  '执行保费与赔款的线下台账核对，确保每笔资金入账清晰、支付可追溯。'
                ]
              }
            }
          },
          services: {
            title: '专业保险经纪服务',
            cards: [
              { icon: '/assets/khb1.png', alt: '全终端覆盖', title: '全渠道服务覆盖', points: ['线上线下全周期保险服务', '保单查询、投保申请、理赔提交随时随地办理'] },
              { icon: '/assets/khb2.png', alt: '多市场覆盖', title: '多产品方案矩阵', points: ['涵盖寿险、健康险、年金、家财险等主流产品', '支持保险+信托、保险+医疗等复合型方案定制'] },
              { icon: '/assets/khb3.png', alt: '实时监控', title: '专属顾问服务', points: ['人工跟进续期、保障检视与额度调整提醒', '由顾问为您同步行业政策变动与产品更新动态'] },
              { icon: '/assets/khb4.png', alt: '专业研究支持', title: '专家顾问团队', points: ['持证人、精算师、理赔专家组成的顾问网络', '家庭风险诊断与个性化方案设计'] },
              { icon: '/assets/khb5.png', alt: '风控保障', title: '专业风险顾问', points: ['提供保障缺口分析与风险预警建议', '理赔进度跟进与争议协商专业支持'] },
              { icon: '/assets/khb6.png', alt: '合规保障', title: '合规服务保障', points: ['规范的线下见证、合同面签与档案合规管理', '客户信息保密与隐私安全管理制度'] }
            ]
          },
          accounts: {
            title: '支持账户类型',
            subtitle: '个人与机构均可开立股票账户',
            items: [
              { icon: '/assets/khd2.png', alt: '个人账户', label: '个人账户' },
              { icon: '/assets/khd2.png', alt: '机构账户', label: '机构账户' }
            ],
            org: {
              title: '开立机构账户',
              desc: '机构账户提供更丰富的交易工具与更灵活的服务。如需开立机构账户，请联系客户经理。'
            }
          }
        },
        'zh-Hant': {
          metaTitle: '保險客戶 - 紅土證券',
          hero: {
            brandName: '紅土證券',
            mainTitle: '保險客戶',
            subtitle: '本頁面內容由集團關聯公司紅土財富提供'
          },
          audiences: {
            title: '專業保險經紀覆蓋全方位人群',
            items: [
              { icon: '/assets/bz1.png', alt: '低費率', text: '高淨值家庭與個人：需要定制化方案，如家族信託、跨境資產配置，注重私密性與長期服務' },
              { icon: '/assets/bz2.png', alt: '專業工具', text: '成長型年輕家庭：首次置業、新婚或育兒階段的家庭，需求集中在重疾險、醫療險、定期壽險、教育金，注重性價比，偏好線上溝通與靈活繳費方式' },
              { icon: '/assets/bz3.png', alt: '廣泛市場', text: '小微企業主與創業者：關注企業財產險、僱主責任險、團體健康險，需平衡個人與企業資產隔離（如股東互保、壽險融資），對條款靈活性和理賠效率要求高' },
              { icon: '/assets/bz4.png', alt: '便捷出入金', text: '專業人士與高知群體：醫生、律師、企業高管等高收入專業人士，偏好數據化分析、條款深度解讀，關注全球醫療資源、高端養老服務' },
              { icon: '/assets/bz5.png', alt: '個性化服務', text: '特定行業從業人員：高風險職業（如建築、貨運）、自由職業者，需定制意外險、職業責任險，常被標準產品排除或加費' },
              { icon: '/assets/bz5.png', alt: '安全保障', text: '互聯網原住民與新中產：習慣線上比價、自主研究條款，關注消費型保險、短期健康險、互聯網定制產品，注重服務響應速度與線上工具使用體驗' }
            ]
          },
          riskInsights: {
            title: '您的核心風險與需求洞察',
            imageAlt: '股票客戶特權',
            points: [
              '健康透支風險：高強度工作、不規律作息，需防範重疾與醫療費用對儲蓄的衝擊。',
              '家庭責任初顯：可能開始贍養父母或組建家庭，需一份高槓桿的意外與壽險保障。',
              '現金流管理：收入增長期，需平衡保障與儲蓄，偏好靈活、高性價比的產品組合。'
            ],
            highlight: '深入溝通，精準識別您的家庭結構、財務狀況與核心風險，奠定科學規劃基礎。',
            buttonText: '了解詳情'
          },
          processFlow: {
            title: '以流程見證專業，以透明構建信任需求分析',
            imageAlt: '工具展示',
            items: [
              { title: '協助投保', desc: '全程指導填寫投保資料，協助健康告知，確保流程合規，從源頭規避未來糾紛。' },
              { title: '保單託管', desc: '為您電子化歸檔所有保單，清晰呈現保障全景，關鍵日期智能提醒，確保保障持續有效。' },
              { title: '協助理賠與維權', desc: '出險時第一時間響應，全程指導並代辦理賠，遇爭議時以專業立場為您據理力爭。' }
            ],
            buttonText: '了解詳情'
          },
          tech: {
            title: '專業的保險經紀服務體系',
            subtitle: '貼心為你搭配專屬保障，智能守護每一次選擇',
            learnMore: '了解更多',
            footnote: '值得信賴的證券經紀服務商',
            tabs: [
              { key: 'cloud', label: '產品動態配置' },
              { key: 'order', label: '保費與賠付管理' },
              { key: 'market', label: '風控與合規審計' },
              { key: 'funds', label: '業務一致性保障' }
            ],
            content: {
              cloud: {
                cardLeft: { title: '保險風控', subtitle: '購力計算' },
                cardRight: { title: '資產可視', subtitle: '風險先知' },
                intro: '支持個性化產品組合與動態定價策略，提升方案配置效率與市場響應能力',
                advantages: [
                  '專業團隊支持根據客戶專業定制、風險偏好、財務狀況進行產品推薦',
                  '保費測算與風險定價模型，基於市場動態核保規則生成報價',
                  '多維度風險校驗與合規篩查，確保方案符合監管與機構承保要求'
                ]
              },
              order: {
                cardLeft: { title: '動態核保', subtitle: '精準賠付' },
                cardRight: { title: '保費精算', subtitle: '風控閉環' },
                intro: '精算模型優化賠付方案，同步提升風險控制精度與理賠服務效率',
                advantages: [
                  '支持批量報價、階梯費率、續保優惠等複雜計費方案定制',
                  '提供線下風險評估、理賠爭議協商與財務對賬服務',
                  '執行人工資金清分、單證核對與機構間賬務稽核'
                ]
              },
              market: {
                cardLeft: { title: '全鏈審計', subtitle: '風控隨行' },
                cardRight: { title: '合規同步', subtitle: '風險洞見' },
                intro: '數據一致性、監管合規化、多源風控與智能審計機制結合，構建全面業務風險防線',
                advantages: [
                  '執行人工盡職調查、背景核實與可疑交易線下審查',
                  '支持黑名單篩查、反洗錢監測、客戶適當性管理，確保全流程業務合規',
                  '支持多維度審計查詢、異常操作告警與自動化合規證據收集'
                ]
              },
              funds: {
                cardLeft: { title: '業務同源', subtitle: '風控一體' },
                cardRight: { title: '跨鏈一致', subtitle: '流程互通' },
                intro: '建立全週期業務連續流程，在關鍵節點執行人工交叉驗證，保障信息一致性。',
                advantages: [
                  '使用統一的線下流程引擎，確保投保單、核保、批改和理賠各環節狀態同步更新。',
                  '設立關鍵操作線下複核與審批環節，確保步驟合規且流程銜接順暢。',
                  '執行保費與賠款的線下賬台核對，確保每筆資金入賬清晰、支付可追溯。'
                ]
              }
            }
          },
          services: {
            title: '專業保險經紀服務',
            cards: [
              { icon: '/assets/khb1.png', alt: '全終端覆蓋', title: '全渠道服務覆蓋', points: ['線上線下全週期保險服務', '保單查詢、投保申請、理賠提交隨時隨地辦理'] },
              { icon: '/assets/khb2.png', alt: '多市場覆蓋', title: '多產品方案矩陣', points: ['涵蓋壽險、健康險、年金、家財險等主流產品', '支持保險+信託、保險+醫療等複合型方案定制'] },
              { icon: '/assets/khb3.png', alt: '實時監控', title: '專屬顧問服務', points: ['人工跟進續期、保障檢視與額度調整提醒', '由顧問為您同步行業政策變動與產品更新動態'] },
              { icon: '/assets/khb4.png', alt: '專業研究支持', title: '專家顧問團隊', points: ['持證人、精算師、理賠專家組成的顧問網絡', '家庭風險診斷與個性化方案設計'] },
              { icon: '/assets/khb5.png', alt: '風控保障', title: '專業風險顧問', points: ['提供保障缺口分析與風險預警建議', '理賠進度跟進與爭議協商專業支持'] },
              { icon: '/assets/khb6.png', alt: '合規保障', title: '合規服務保障', points: ['規範的線下見證、合同面簽與檔案合規管理', '客戶信息保密與隱私安全管理制度'] }
            ]
          },
          accounts: {
            title: '支持賬戶類型',
            subtitle: '個人與機構均可開立股票賬戶',
            items: [
              { icon: '/assets/khd2.png', alt: '個人賬戶', label: '個人賬戶' },
              { icon: '/assets/khd2.png', alt: '機構賬戶', label: '機構賬戶' }
            ],
            org: {
              title: '開立機構賬戶',
              desc: '機構賬戶提供更豐富的交易工具與更靈活的服務。如需開立機構賬戶，請聯繫客戶經理。'
            }
          }
        },
        en: {
          metaTitle: 'Insurance Customers - Redland Securities',
          hero: {
            brandName: 'Redland Securities',
            mainTitle: 'Insurance Customers',
            subtitle: 'The content of this page is provided by Redland Wealth, an affiliate of the Group'
          },
          audiences: {
            title: 'Professional Insurance Brokerage Covering Every Type of Client',
            items: [
              { icon: '/assets/bz1.png', alt: 'Low fees', text: 'High-net-worth families and individuals: need customized solutions such as family trusts and cross-border asset allocation, with a focus on privacy and long-term service' },
              { icon: '/assets/bz2.png', alt: 'Professional tools', text: 'Growing young families: families buying their first home, newly married or raising children, whose needs focus on critical illness, medical, term life and education-savings insurance; value-conscious, preferring online communication and flexible premium payments' },
              { icon: '/assets/bz3.png', alt: 'Wide market access', text: 'Small and micro business owners and entrepreneurs: focus on business property, employer\'s liability and group health insurance; need to balance the separation of personal and business assets (e.g. shareholder cross-insurance, life insurance financing), and demand flexible terms and efficient claims' },
              { icon: '/assets/bz4.png', alt: 'Convenient deposits and withdrawals', text: 'Professionals and the well-educated: high-income professionals such as doctors, lawyers and executives, who prefer data-driven analysis and in-depth interpretation of policy terms, and care about global medical resources and premium senior-care services' },
              { icon: '/assets/bz5.png', alt: 'Personalized service', text: 'Workers in specific industries: those in high-risk occupations (e.g. construction, freight) and freelancers, who need customized accident and professional liability insurance and are often excluded from or surcharged by standard products' },
              { icon: '/assets/bz5.png', alt: 'Security assurance', text: 'Digital natives and the new middle class: accustomed to comparing prices online and studying terms on their own; focus on consumer-type, short-term health and internet-customized insurance, valuing responsive service and the user experience of online tools' }
            ]
          },
          riskInsights: {
            title: 'Insights into Your Core Risks and Needs',
            imageAlt: 'Stock customer privileges',
            points: [
              'Health depletion risk: high-intensity work and irregular routines call for protection against the impact of critical illness and medical costs on your savings.',
              'Emerging family responsibilities: as you may begin supporting your parents or start a family, you need high-leverage accident and life insurance protection.',
              'Cash flow management: during a period of income growth, balance protection and savings, preferring flexible, cost-effective product combinations.'
            ],
            highlight: 'Through in-depth communication, we precisely identify your family structure, financial situation and core risks, laying the foundation for sound planning.',
            buttonText: 'Learn More'
          },
          processFlow: {
            title: 'Professionalism Proven by Process, Trust Built Through Transparency — Needs Analysis',
            imageAlt: 'Tool showcase',
            items: [
              { title: 'Application Assistance', desc: 'Guide you through completing application materials and assist with health disclosures to ensure a compliant process and avoid future disputes at the source.' },
              { title: 'Policy Custody', desc: 'Digitally archive all your policies with a clear overview of your coverage and smart reminders for key dates, ensuring your protection stays effective.' },
              { title: 'Claims Assistance & Advocacy', desc: 'Respond immediately when a claim occurs, guide and handle claims on your behalf throughout, and argue your case professionally when disputes arise.' }
            ],
            buttonText: 'Learn More'
          },
          tech: {
            title: 'A Professional Insurance Brokerage Service System',
            subtitle: 'Thoughtfully tailored protection for you, intelligently safeguarding every choice',
            learnMore: 'Learn More',
            footnote: 'A Securities Brokerage Service Provider You Can Trust',
            tabs: [
              { key: 'cloud', label: 'Dynamic Product Configuration' },
              { key: 'order', label: 'Premium & Claims Management' },
              { key: 'market', label: 'Risk Control & Compliance Audit' },
              { key: 'funds', label: 'Business Consistency Assurance' }
            ],
            content: {
              cloud: {
                cardLeft: { title: 'Insurance Risk Control', subtitle: 'Purchasing Power Calculation' },
                cardRight: { title: 'Asset Visibility', subtitle: 'Risk Foresight' },
                intro: 'Supports personalized product combinations and dynamic pricing strategies, improving solution configuration efficiency and market responsiveness.',
                advantages: [
                  'A professional team recommends products based on each client\'s profession, risk appetite and financial situation',
                  'Premium calculation and risk pricing models generate quotes based on market-dynamic underwriting rules',
                  'Multi-dimensional risk validation and compliance screening ensure solutions meet regulatory and institutional underwriting requirements'
                ]
              },
              order: {
                cardLeft: { title: 'Dynamic Underwriting', subtitle: 'Precision Claims' },
                cardRight: { title: 'Actuarial Pricing', subtitle: 'Closed-Loop Risk Control' },
                intro: 'Actuarial models optimize claims solutions while improving risk-control precision and claims service efficiency.',
                advantages: [
                  'Supports customized complex billing schemes such as bulk quotes, tiered rates and renewal discounts',
                  'Provides offline risk assessment, claims dispute negotiation and financial reconciliation services',
                  'Performs manual fund settlement, document verification and inter-institution account audits'
                ]
              },
              market: {
                cardLeft: { title: 'Full-Chain Audit', subtitle: 'Risk Control On the Go' },
                cardRight: { title: 'Compliance Sync', subtitle: 'Risk Insight' },
                intro: 'Combining data consistency, regulatory compliance, multi-source risk control and intelligent auditing to build a comprehensive business risk defense line.',
                advantages: [
                  'Performs manual due diligence, background verification and offline review of suspicious transactions',
                  'Supports blacklist screening, anti-money-laundering monitoring and client suitability management to ensure end-to-end compliance',
                  'Supports multi-dimensional audit queries, anomaly alerts and automated compliance evidence collection'
                ]
              },
              funds: {
                cardLeft: { title: 'Same Business Source', subtitle: 'Integrated Risk Control' },
                cardRight: { title: 'Cross-Chain Consistency', subtitle: 'Interconnected Processes' },
                intro: 'Establishes full-lifecycle business continuity processes with manual cross-verification at key nodes to ensure information consistency.',
                advantages: [
                  'A unified offline process engine keeps application, underwriting, endorsement and claims statuses synchronized',
                  'Key operations go through offline review and approval to ensure compliant steps and smooth process handoffs',
                  'Offline ledger reconciliation of premiums and claims ensures every fund entry is clear and payments traceable'
                ]
              }
            }
          },
          services: {
            title: 'Professional Insurance Brokerage Services',
            cards: [
              { icon: '/assets/khb1.png', alt: 'Full terminal coverage', title: 'Omnichannel Service Coverage', points: ['Full-cycle insurance services online and offline', 'Policy inquiries, applications and claims submissions handled anytime, anywhere'] },
              { icon: '/assets/khb2.png', alt: 'Multi-market coverage', title: 'Multi-Product Solution Matrix', points: ['Covers mainstream products including life, health, annuity and home property insurance', 'Supports customized hybrid solutions such as insurance + trust and insurance + healthcare'] },
              { icon: '/assets/khb3.png', alt: 'Real-time monitoring', title: 'Dedicated Advisory Service', points: ['Manual follow-up on renewals, coverage reviews and limit adjustment reminders', 'Advisors keep you informed of industry policy changes and product updates'] },
              { icon: '/assets/khb4.png', alt: 'Professional research support', title: 'Expert Advisory Team', points: ['A network of licensed practitioners, actuaries and claims experts', 'Family risk diagnosis and personalized solution design'] },
              { icon: '/assets/khb5.png', alt: 'Risk-control assurance', title: 'Professional Risk Advisory', points: ['Provides coverage gap analysis and risk early-warning advice', 'Professional support for claims progress follow-up and dispute negotiation'] },
              { icon: '/assets/khb6.png', alt: 'Compliance assurance', title: 'Compliance Service Assurance', points: ['Standardized offline witnessing, contract signing and compliant archive management', 'Client information confidentiality and privacy security management'] }
            ]
          },
          accounts: {
            title: 'Supported Account Types',
            subtitle: 'Both individuals and institutions can open stock accounts',
            items: [
              { icon: '/assets/khd2.png', alt: 'Individual account', label: 'Individual Account' },
              { icon: '/assets/khd2.png', alt: 'Institutional account', label: 'Institutional Account' }
            ],
            org: {
              title: 'Open an Institutional Account',
              desc: 'Institutional accounts offer richer trading tools and more flexible services. To open an institutional account, please contact your account manager.'
            }
          }
        }
      }
    };
  },
  mounted() {
    this.currentLocale = (this.$i18n && this.$i18n.locale) || 'zh';
    if (window.addEventListener) {
      window.addEventListener('localeChange', this.handleLocaleChange);
    }
  },
  beforeDestroy() {
    if (window.removeEventListener) {
      window.removeEventListener('localeChange', this.handleLocaleChange);
    }
  },
  methods: {
    resolveLink(url) {
      if (typeof url === 'string' && url.trim()) {
        return url.trim();
      }
      return this.defaultTradingUrl;
    },
    handleLocaleChange() {
      this.currentLocale = (this.$i18n && this.$i18n.locale) || 'zh';
    }
  },
  computed: {
    c() {
      return this.messages[this.currentLocale] || this.messages.zh;
    },
    currentTech() {
      return this.c.tech.content[this.currentTechTab] || this.c.tech.content.cloud;
    }
  }
};
</script>

<style scoped>
.hero-banner { position: relative; height: 40vh; min-height: 600px; overflow: hidden; background:url("@/assets/487585e4e8bd59c74850991ff73e9fe8.jpg"); background-size: cover; background-position: center; background-repeat: no-repeat; }
.banner-content { position: absolute; top: 50%; left: 15%; transform: translateY(-50%); z-index: 10; }
.text-content { text-align: left; }
.brand-name { font-size: 1.5rem; font-weight: 300; color: white; margin-bottom: 0.5rem; letter-spacing: 0.1em; }
.main-title { font-size: 3.5rem; font-weight: 700; color: white; margin-bottom: 0.5rem; letter-spacing: 0.05em; line-height: 1.1; }
.subtitle { font-size: 1.2rem; font-weight: 300; color: white; margin-bottom: 2rem; letter-spacing: 0.05em; opacity: 0.9; }
.cta-button { background: transparent; color: white; border: 2px solid white; padding: 0.75rem 2rem; font-size: 1rem; font-weight: 500; border-radius: 4px; cursor: pointer; transition: all 0.3s ease; letter-spacing: 0.05em; }
.cta-button:hover { background: white; color: #1e3a8a; transform: translateY(-2px); box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2); }
@media (max-width: 1024px) { .hero-banner { height: 80vh; min-height: 500px; } .main-title { font-size: 2.5rem; } .banner-content { left: 10%; } }
@media (max-width: 768px) { .hero-banner { height: 70vh; min-height: 400px; } .main-title { font-size: 2rem; } .brand-name { font-size: 1.2rem; } .banner-content { left: 8%; } .cta-button { padding: 0.6rem 1.5rem; font-size: 0.9rem; } }
@media (max-width: 640px) { .hero-banner { height: 60vh; min-height: 350px; } .main-title { font-size: 1.8rem; } .brand-name { font-size: 1rem; } .banner-content { left: 5%; } }
.nav-tab { padding: 0.75rem 1rem; font-weight: 500; color: #6b7280; border-bottom: 2px solid transparent; transition: all 0.2s; position: relative; cursor: pointer; background: none; border: none; }
.nav-tab.active { color: #1f2937; border-bottom-color: #1f2937; }
.nav-tab:hover { color: #374151; }
.tech-card { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 0.75rem; }
.tech-card-body { padding: 1.5rem; text-align: center; }
.tech-title { font-size: 1.75rem; font-weight: 700; color: #ef7c5b; }
.tech-subtitle { margin-top: 0.25rem; color: #9ca3af; }
.advantage-icon-box { width: 48px; height: 48px; min-width: 48px; display: inline-flex; align-items: center; justify-content: center; border: 2px dashed #ef7c5b; border-radius: 12px; margin-right: 12px; background: #fff7f4; }
.learn-more-btn { background: #ef7c5b; color: #ffffff; padding: 0.6rem 1.5rem; border: none; border-radius: 1.5rem; font-weight: 600; }
</style>
